import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Form, Input, Radio, Rate, Steps, Typography } from 'antd';
import { loadSurveys, submitResponse, hasVoted } from '../api/surveys';

const { Title, Text } = Typography;

export default function TakeSurvey() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const survey = loadSurveys().find(s => s.id === id);
  const [answers, setAnswers] = useState<Record<string, string | string[] | number>>({});
  const [current, setCurrent] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!survey) return <div style={{ padding: 24 }}><Text type="danger">Survey not found.</Text></div>;
  if (hasVoted(survey.id)) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="You've already responded to this survey." action={
          <Button onClick={() => navigate(`/results/${survey.id}`)}>View Results</Button>
        } />
      </div>
    );
  }
  if (survey.closedAt) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" message="This survey is closed." action={
          <Button onClick={() => navigate(`/results/${survey.id}`)}>View Results</Button>
        } />
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ padding: 24, maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
        <Title level={3}>Thank you for responding!</Title>
        <Text type="secondary">Your response has been recorded.</Text>
        <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button onClick={() => navigate('/')}>Back to Surveys</Button>
          <Button type="primary" onClick={() => navigate(`/results/${survey.id}`)}>See Results</Button>
        </div>
      </div>
    );
  }

  const q = survey.questions[current];
  const isLast = current === survey.questions.length - 1;

  function setAnswer(val: string | string[] | number) {
    setAnswers(prev => ({ ...prev, [q.id]: val }));
    setError('');
  }

  function next() {
    if (q.required && (answers[q.id] === undefined || answers[q.id] === '')) {
      setError('This question is required.');
      return;
    }
    if (isLast) {
      submitResponse(survey!.id, answers);
      setSubmitted(true);
    } else {
      setCurrent(c => c + 1);
      setError('');
    }
  }

  const stepItems = survey.questions.map((sq, i) => ({
    title: `Q${i + 1}`,
    status: (i < current ? 'finish' : i === current ? 'process' : 'wait') as 'finish' | 'process' | 'wait',
  }));

  return (
    <div style={{ padding: 24, maxWidth: 680, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 4 }}>{survey.title}</Title>
      <Text type="secondary">{survey.description}</Text>

      <Steps items={stepItems} style={{ margin: '20px 0' }} size="small" />

      <Card>
        <Text strong style={{ fontSize: 16 }}>
          {current + 1}. {q.text}
          {q.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
        </Text>

        <div style={{ marginTop: 16 }}>
          {q.type === 'single' && (
            <Radio.Group
              value={answers[q.id] as string}
              onChange={e => setAnswer(e.target.value)}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {q.options?.map(opt => (
                <Radio key={opt} value={opt} style={{ padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, margin: 0 }}>
                  {opt}
                </Radio>
              ))}
            </Radio.Group>
          )}

          {q.type === 'multiple' && (
            <Checkbox.Group
              value={answers[q.id] as string[]}
              onChange={vals => setAnswer(vals as string[])}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {q.options?.map(opt => (
                <Checkbox key={opt} value={opt} style={{ padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, margin: 0 }}>
                  {opt}
                </Checkbox>
              ))}
            </Checkbox.Group>
          )}

          {q.type === 'rating' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Rate
                count={q.maxRating ?? 5}
                value={answers[q.id] as number}
                onChange={v => setAnswer(v)}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {answers[q.id] ? `${answers[q.id]} / ${q.maxRating ?? 5}` : 'Select a rating'}
              </Text>
            </div>
          )}

          {q.type === 'text' && (
            <Input.TextArea
              rows={4}
              value={answers[q.id] as string}
              onChange={e => setAnswer(e.target.value)}
              placeholder="Your answer..."
            />
          )}
        </div>

        {error && <Alert type="error" message={error} style={{ marginTop: 12 }} />}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <Button onClick={() => setCurrent(c => c - 1)} disabled={current === 0}>Previous</Button>
          <Button type="primary" onClick={next}>
            {isLast ? 'Submit' : 'Next'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
